<script setup lang="ts">
import * as R from 'ramda'
import { computed, ref, watch, watchEffect } from 'vue'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@collector/shared-ui'
import { injectSportsApiClient } from '@collector/shared-ui-domain'
import type { SubParticipantsPatchPayload } from '@collector/sportsapi-client'
import {
  SubParticipantPosition,
  YesNoEnum,
  type UpdateEventParticipantsObject,
} from '@collector/sportsapi-client-legacy'
import type { Participant, SubParticipant } from '@collector/sportsapi-types'

import type { SportsStatConfig } from '../../interfaces/SportStatConfig'
import { getStatById } from '../../utils'
import Footer from '../common/Footer.vue'
import PlayerStatistics from './PlayerStatistics/PlayerStatistics.vue'
import TeamStatistics from './TeamStatistics/TeamStatistics.vue'

// TODO: refactor to update only changed stats instead of whole participants

type SubmitTeamStatsOpts = {
  home?: Participant
  away?: Participant
}

type Props = {
  eventId: number
  teamStats: SportsStatConfig[]
  teamStatsAccordion: SportsStatConfig[]
  playerStats: SportsStatConfig[]
  isStatsVerified?: boolean
  isVerifyStatsButtonDisabled?: boolean
  teamColorsSwitched?: boolean
}
const props = defineProps<Props>()

type Emits = {
  saved: [void]
}
const emit = defineEmits<Emits>()
const forceMount = ref(false)

// composables
const sportsApiClient = injectSportsApiClient()

// state
const homeParticipantModel = defineModel<Participant>('homeParticipant', {
  required: true,
})
const awayParticipantModel = defineModel<Participant>('awayParticipant', {
  required: true,
})

const homeParticipant = ref(R.clone(homeParticipantModel.value))
const awayParticipant = ref(R.clone(awayParticipantModel.value))
const homePlayersModel = defineModel<SubParticipant[]>('homePlayers', {
  required: true,
})
const awayPlayersModel = defineModel<SubParticipant[]>('awayPlayers', {
  required: true,
})
const dirty = defineModel<boolean>('dirty', {
  default: false,
})

const homePlayers = ref(
  R.clone(filterOutCoachAndWillNotPlay(homePlayersModel.value)),
)
const awayPlayers = ref(
  R.clone(filterOutCoachAndWillNotPlay(awayPlayersModel.value)),
)

const homeTeamDirty = ref(false)
const awayTeamDirty = ref(false)

const homePlayersDirty = ref(false)
const awayPlayersDirty = ref(false)

// computed
const teamDirty = computed(() => homeTeamDirty.value || awayTeamDirty.value)
const playersDirty = computed(
  () => homePlayersDirty.value || awayPlayersDirty.value,
)

// methods
async function verify(): Promise<void> {
  await submit()
  await sportsApiClient.Events.patch(props.eventId, {
    event: { is_stats_verified: YesNoEnum.Yes },
  })
}

async function submit(): Promise<void> {
  if (playersDirty.value) {
    const playersToSubmit: SubParticipant[] = []
    if (homePlayersDirty.value) {
      playersToSubmit.push(...homePlayers.value)
    }
    if (awayPlayersDirty.value) {
      playersToSubmit.push(...awayPlayers.value)
    }
    await submitPlayerStats(playersToSubmit)
  }

  if (teamDirty.value) {
    const opts: SubmitTeamStatsOpts = {}
    if (homeTeamDirty.value) {
      opts.home = homeParticipant.value
    }
    if (awayTeamDirty.value) {
      opts.away = awayParticipant.value
    }
    await submitTeamStats(opts)
  }
  emit('saved')
}

async function submitTeamStats({
  home,
  away,
}: SubmitTeamStatsOpts): Promise<void> {
  const homeParticipantsPayload = home && getParticipantPayload(home)
  const awayParticipantsPayload = away && getParticipantPayload(away)
  const payloads = []

  if (homeParticipantsPayload) payloads.push(homeParticipantsPayload)
  if (awayParticipantsPayload) payloads.push(awayParticipantsPayload)
  if (payloads.length === 0) return

  await sportsApiClient.Events.Participants.patch(props.eventId, payloads)

  homeParticipantModel.value = homeParticipant.value
  awayParticipantModel.value = awayParticipant.value
}

function getParticipantPayload(
  participant: Participant,
): Omit<UpdateEventParticipantsObject, 'subparticipants'> {
  const teamStatsAll = [...props.teamStats, ...props.teamStatsAccordion]
  return {
    id: participant.id,
    results: [],
    stats: teamStatsAll.map((c) => getStatById(c.sportStat.id, participant)),
  }
}

function getPlayersPayload(
  players: SubParticipant[],
): SubParticipantsPatchPayload[] {
  return players.map((player) => ({
    id: player.id,
    lineup_id: player.lineup_id,
    team_id: player.team_id,
    short_name: player.short_name,
    stats: player.stats.filter((s) => s.value !== null),
  }))
}

async function submitPlayerStats(players: SubParticipant[]): Promise<void> {
  await sportsApiClient.Events.SubParticipants.patch(
    props.eventId,
    getPlayersPayload(players),
    false,
  )
  homePlayersModel.value = homePlayers.value
  awayPlayersModel.value = awayPlayers.value
}

function filterOutCoachAndWillNotPlay(
  players: SubParticipant[],
): SubParticipant[] {
  return players.filter(
    (p) =>
      p.position !== SubParticipantPosition.Coach &&
      p.position !== SubParticipantPosition.WillNotPlay,
  )
}

// watch
watch(homeParticipantModel, (n) => {
  if (!homeTeamDirty.value) homeParticipant.value = R.clone(n)
})
watch(awayParticipantModel, (n) => {
  if (!awayTeamDirty.value) awayParticipant.value = R.clone(n)
})
watch(homePlayersModel, (n) => {
  if (!homePlayersDirty.value)
    homePlayers.value = R.clone(filterOutCoachAndWillNotPlay(n))
})
watch(awayPlayersModel, (n) => {
  if (!awayPlayersDirty.value)
    awayPlayers.value = R.clone(filterOutCoachAndWillNotPlay(n))
})

watchEffect(() => {
  dirty.value = teamDirty.value || playersDirty.value
})
</script>

<template>
  <div class="relative grid grid-cols-1 grid-rows-[auto_1fr_auto] gap-2">
    <slot name="summary"></slot>
    <Tabs
      class="gap-2"
      defaultValue="team"
    >
      <TabsList class="grid w-full grid-cols-2">
        <TabsTrigger
          class="grow"
          value="team"
        >
          Team stats
        </TabsTrigger>
        <TabsTrigger
          class="grow"
          value="player"
          @mouseenter="forceMount = true"
        >
          Player stats
        </TabsTrigger>
      </TabsList>
      <div>
        <TabsContent
          class="flex justify-center"
          value="team"
        >
          <TeamStatistics
            v-model:homeTeamDirty="homeTeamDirty"
            v-model:awayTeamDirty="awayTeamDirty"
            v-model:homeParticipant="homeParticipant"
            v-model:awayParticipant="awayParticipant"
            class="w-full max-w-144"
            :teamStats
            :teamStatsAccordion
            :initialHomeParticipant="homeParticipantModel"
            :initialAwayParticipant="awayParticipantModel"
          />
        </TabsContent>
        <TabsContent
          value="player"
          :forceMount
        >
          <PlayerStatistics
            v-model:homePlayersDirty="homePlayersDirty"
            v-model:awayPlayersDirty="awayPlayersDirty"
            v-model:homePlayers="homePlayers"
            v-model:awayPlayers="awayPlayers"
            :initialHomePlayers="homePlayersModel"
            :initialAwayPlayers="awayPlayersModel"
            :homeParticipant="homeParticipantModel"
            :awayParticipant="awayParticipantModel"
            :playerStats
            :teamColorsSwitched
          />
        </TabsContent>
      </div>
    </Tabs>
    <Footer
      :dirty
      :isStatsVerified
      :isVerifyStatsButtonDisabled
      @submit="submit"
      @verify="verify"
    />
  </div>
</template>
